import * as Yup from 'yup';
import { addMethod } from 'yup';
import {
    SellerActionTypeGroups,
    SellerAndBuyerActionTypeGroups,
    MarketplaceEventActionTypeList,
    TimelineActionType,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
export type UniqnessLevel = 'timeline' | 'stage';

declare module 'yup' {
    interface NumberSchema {
        uniqueActionValidation(
            timelineEvents: TimelineEvent[],
            currentEvent: TimelineEvent
        ): NumberSchema;
        uniqueGroupValidation(
            timelineEvents: TimelineEvent[],
            currentEvent: TimelineEvent
        ): NumberSchema;
    }
}

// Extend Yup with a custom methods
addMethod(
    Yup.number,
    'uniqueActionValidation',
    function (timelineEvents: TimelineEvent[], currentEvent: TimelineEvent) {
        return this.test(
            'interest-expression-action-validation',
            '',
            function (value) {
                const isActionChanged =
                    value !== currentEvent?.linkedAction?.actionType;

                if (!isActionChanged) {
                    return true;
                }

                const uniqnessLevel = getUniquenessLevel(value);
                if (uniqnessLevel === 'stage') {
                    return validateStageUniqeAction(
                        value,
                        timelineEvents,
                        currentEvent,
                        this
                    );
                } else {
                    return validateTimelineUniqueAction(
                        value,
                        timelineEvents,
                        this
                    );
                }
            }
        );
    }
);

addMethod(
    Yup.number,
    'uniqueGroupValidation',
    function (timelineEvents: TimelineEvent[], currentEvent: TimelineEvent) {
        return this.test(
            'buyer-selection-action-validation',
            '',
            function (value) {
                const isActionChanged =
                    value !== currentEvent?.linkedAction?.actionType;

                if (!isActionChanged) {
                    return true;
                }

                return validateTimelineUniqeGroup(
                    value,
                    timelineEvents,
                    currentEvent,
                    this
                );
            }
        );
    }
);

function validateTimelineUniqueAction(
    value: number,
    timelineEvents: TimelineEvent[],
    yupContext: Yup.TestContext<Yup.AnyObject>
) {
    const { path, createError } = yupContext;
    const actions = timelineEvents
        ?.flatMap((ev) => ev.linkedAction)
        .filter((f) => f);
    const message = 'The selected action type is already in use for timeline';
    const actionDuplicate = actions.find((f) => f.actionType === value);

    if (actionDuplicate) {
        return createError({ path, message });
    }

    return true;
}

function validateStageUniqeAction(
    value: number,
    timelineEvents: TimelineEvent[],
    currentEvent: TimelineEvent,
    yupContext: Yup.TestContext<Yup.AnyObject>
) {
    const { path, createError } = yupContext;

    const message =
        'The selected action type is already in use for the same stage';
    const actionDuplicate = timelineEvents.find(
        (f) =>
            f?.linkedAction?.actionType === value &&
            f.stage === currentEvent.stage
    );

    if (actionDuplicate) {
        return createError({ path, message });
    }

    return true;
}

function validateTimelineUniqeGroup(
    value: number,
    timelineEvents: TimelineEvent[],
    currentEvent: TimelineEvent,
    yupContext: Yup.TestContext<Yup.AnyObject>
) {
    const { path, createError } = yupContext;

    const stageUniqActionTypeGroup = [
        SellerAndBuyerActionTypeGroups.BuyerSelection,
        SellerActionTypeGroups.BuyerSelection,
        SellerAndBuyerActionTypeGroups.NdaTermsSelection,
    ];

    const message = 'The selected option is already in use for the same stage';

    const actionType = MarketplaceEventActionTypeList.find(
        (x) => x.id === value
    );
    const group = actionType ? actionType.group : undefined;

    const isStageUniqueActionTypeGroup = stageUniqActionTypeGroup.some(
        (s) => s === group
    );

    if (isStageUniqueActionTypeGroup) {
        const isGroupAlreadyUsedInStage = timelineEvents.some(
            (e) =>
                e.id !== currentEvent.id &&
                e.party === currentEvent.party &&
                e.linkedAction !== null &&
                MarketplaceEventActionTypeList.find(
                    (x) => x.id === e.linkedAction?.actionType
                )?.group === group &&
                e.stage === currentEvent.stage
        );

        if (isGroupAlreadyUsedInStage) {
            return createError({ path, message });
        }
    }

    return true;
}

function getUniquenessLevel(actionType: TimelineActionType): UniqnessLevel {
    const stageValidationList: TimelineActionType[] = [
        TimelineActionType.BuyerExpressionOfInterestWithRange,
    ];

    const isStageValidation = stageValidationList.some((s) => s === actionType);

    return isStageValidation ? 'stage' : 'timeline';
}
